import React, { useEffect, useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import Location from 'utils/Location';
import Modal from 'components/Modal';
import { Text, Button, Divider } from 'components/ui';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import initiateAccountLink from 'services/api/loyaltyConnection/initiateAccountLink';
import { getLinkFailureUrl } from 'components/LoyaltyConnection/constants';
import LoyaltyConnectionEN from 'components/LoyaltyConnection/locales/LoyaltyConnection_en_US';
import LoyaltyConnectionFR from 'components/LoyaltyConnection/locales/LoyaltyConnection_fr_CA';

/**
 * Link Account (Notice & Consent) modal for the Sephora x Starbucks loyalty flow.
 * User must agree to the terms before the "Link Account" action is enabled.
 *
 * On confirm, calls the initiateAccountLink mutation and hands the returned
 * partner OAuth URL to the container via `onOAuthUrl`, which redirects the whole
 * window to that URL (no iframe). If the mutation fails (or returns no URL), the
 * window is redirected to the failure URL (/starbucks?isSuccess=false) so the
 * landing page shows the error popup (LC-145).
 */
const LinkAccountModal = ({ isOpen, onDismiss, onOAuthUrl, title }) => {
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const getText = localeUtils.getLocalisedDataFromResource(LoyaltyConnectionEN, LoyaltyConnectionFR);

    // Reset the consent checkbox each time the modal closes, so the next open
    // always starts fresh (unchecked).
    useEffect(() => {
        if (!isOpen) {
            setAgreed(false);
            setSubmitting(false);
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        if (!agreed || submitting) {
            return;
        }

        setSubmitting(true);

        try {
            const partnerOAuthUrl = await initiateAccountLink();

            if (partnerOAuthUrl) {
                onOAuthUrl(partnerOAuthUrl);
            } else {
                Location.setLocation(getLinkFailureUrl());
            }
        } catch (error) {
            Sephora.logger.error('[LoyaltyConnection] initiateAccountLink failed:', error);
            Location.setLocation(getLinkFailureUrl());
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            maxWidth={['100%', '100%', 460]}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{title || getText('linkModalTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body paddingBottom={[5, 5, 6]}>
                <Text
                    is='h2'
                    fontWeight='bold'
                    fontSize='md'
                    marginBottom={3}
                >
                    {getText('linkModalSubtitle')}
                </Text>

                <Text
                    is='p'
                    fontSize='sm'
                    lineHeight='normal'
                    marginBottom={4}
                >
                    {getText('linkModalBody')}
                </Text>

                <Divider marginY={4} />

                <Checkbox
                    checked={agreed}
                    onChange={() => setAgreed(prev => !prev)}
                    marginBottom={4}
                    data-at={Sephora.debug.dataAt('starbucks_link_agree_checkbox')}
                >
                    {getText('linkModalCheckbox')}
                </Checkbox>

                <Button
                    variant='primary'
                    width='100%'
                    disabled={!agreed || submitting}
                    onClick={handleSubmit}
                    data-at={Sephora.debug.dataAt('starbucks_link_account_button')}
                >
                    {getText('linkModalSubmit')}
                </Button>
            </Modal.Body>
        </Modal>
    );
};

export default wrapFunctionalComponent(LinkAccountModal, 'LinkAccountModal');
